import { useMemo, useState } from 'react';
import type { Color, CommanderPick, PowerLevel } from '@edh/shared';
import {
  COLOR_HEX,
  COLORS,
  MAX_DECK_NICKNAME,
  PLACEMENT_GAMES,
  PAIR_JOIN,
  POWER_LEVELS,
  commandersOf,
  deckLabel,
  deckRecord,
  decksNeedingAGame,
  display,
  identityGradient,
  formatRecord,
  games,
  isRanked,
  roman,
  sortColors,
  winRate,
} from '@edh/shared';
import { CommanderSearch } from '../components/CommanderSearch';
import { DeckThumb } from '../components/DeckThumb';
import { RankedAvatar } from '../components/RankedAvatar';
import { Sheet } from '../components/Sheet';
import { fetchArt } from '../lib/api';
import { fileToDeckArt } from '../lib/avatar';
import { writeArt } from '../lib/db';
import { navigate } from '../lib/router';
import { useStore } from '../lib/store';
import type { Deck } from '@edh/shared';

/** How the pool can be ordered. Name is the default because it is the only one
 *  that does not move under you between visits. */
type Sort = 'name' | 'rating' | 'winRate' | 'color' | 'played';

const SORTS: { key: Sort; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'rating', label: 'Rating' },
  { key: 'winRate', label: 'Win rate' },
  { key: 'color', label: 'Colour' },
  { key: 'played', label: 'Played' },
];

export function Decks() {
  const { state } = useStore();
  const [adding, setAdding] = useState(false);
  const [showRetired, setShowRetired] = useState(false);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>('name');
  const [colorFilter, setColorFilter] = useState<Color | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  /**
   * Whether the list is being narrowed by something folded out of sight.
   *
   * The point of the dot on the toggle. A list quietly showing only the green
   * decks, sorted by win rate, with no visible reason why, is the failure mode
   * of hiding controls — so the button says when it is doing something. Sorting
   * by name is the default and does not count as narrowing.
   */
  const narrowed = colorFilter !== null || sort !== 'name';

  const decks = useMemo(() => {
    const needle = query.trim().toLowerCase();
    // Unplayed decks sort last rather than perfect: no games is not a 0% record
    // and it is certainly not a 100% one.
    const rate = (d: Deck) => winRate(deckRecord(state.matches, d.id)) ?? -1;
    const played = (d: Deck) => games(deckRecord(state.matches, d.id));

    return state.decks
      .filter((d) => showRetired || !d.retired)
      .filter(
        (d) =>
          !needle ||
          deckLabel(d).toLowerCase().includes(needle) ||
          d.name.toLowerCase().includes(needle),
      )
      // A colour filter means "contains", not "is" — asking for blue decks and
      // being shown only mono-blue is nobody's idea of the question.
      .filter((d) => !colorFilter || d.colors.includes(colorFilter))
      .sort((a, b) => {
        switch (sort) {
          case 'rating':
            return b.elo - a.elo;
          case 'winRate':
            return rate(b) - rate(a) || deckLabel(a).localeCompare(deckLabel(b));
          case 'played':
            return played(b) - played(a) || deckLabel(a).localeCompare(deckLabel(b));
          case 'color':
            // Fewer colours first, then by the canonical WUBRG order, so the
            // list reads as a colour wheel rather than an alphabet.
            return (
              a.colors.length - b.colors.length ||
              a.colors.join('').localeCompare(b.colors.join('')) ||
              deckLabel(a).localeCompare(deckLabel(b))
            );
          default:
            return deckLabel(a).localeCompare(deckLabel(b));
        }
      });
  }, [state.decks, state.matches, showRetired, query, sort, colorFilter]);

  const retiredCount = state.decks.filter((d) => d.retired).length;
  const total = state.decks.filter((d) => !d.retired).length;

  return (
    <>
      <div className="section-head">
        <h2>The pool</h2>
        <span className="hint num">
          {decks.length === total ? total : `${decks.length}/${total}`}
        </span>
      </div>

      <NeedsAGame />

      {/* Search and sort appear once there are enough decks for either to be
          worth the space. Three decks do not need filtering. */}
      {total > 4 && (
        <div className="pool-controls">
          {/*
            Search, and everything else folded away beside it.

            Two rows of chips sat here permanently: five sort options and five
            colours, on a screen whose job is showing decks. They are worth
            having and they are not worth a third of the viewport before
            anybody has asked for them — sorting by name and filtering by
            nothing is the right answer almost every time it is opened.
          */}
          <div className="pool-search">
            <input
              className="input"
              type="search"
              placeholder="Search commanders"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button
              className={`pool-filter-toggle${filtersOpen ? ' open' : ''}${
                narrowed ? ' active' : ''
              }`}
              aria-expanded={filtersOpen}
              aria-label="Sort and filter"
              onClick={() => setFiltersOpen((v) => !v)}
            >
              {/* Three lines, shortest last — the shape of something being
                  narrowed, which is what these do. A hamburger of equal bars
                  means a menu, and this is not one. */}
              <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true">
                <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M3 6h14" />
                  <path d="M5.5 10h9" />
                  <path d="M8 14h4" />
                </g>
              </svg>
              {/* A dot rather than a count: there is at most one colour and one
                  sort, so a number would only ever say "1". */}
              {narrowed && <span className="pool-filter-dot" aria-label="filters applied" />}
            </button>
          </div>

          {filtersOpen && (
            <div className="pool-filters">
              <div className="chips">
                {SORTS.map((s) => (
                  <button
                    key={s.key}
                    className="chip"
                    aria-pressed={sort === s.key}
                    onClick={() => setSort(s.key)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              <div className="color-filter">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    className="color-dot"
                    aria-pressed={colorFilter === c}
                    aria-label={`only ${c} decks`}
                    style={{ background: COLOR_HEX[c] }}
                    onClick={() => setColorFilter((cur) => (cur === c ? null : c))}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {decks.length === 0 ? (
        <div className="empty">
          {total === 0 ? (
            <>
              <strong>The pool is empty</strong>
              Name a commander and the colours, the art, and a starting rating come with it.
            </>
          ) : (
            <>
              <strong>Nothing matches</strong>
              No deck here is called that, or wears that colour.
            </>
          )}
        </div>
      ) : (
        <div className="list">
          {decks.map((deck) => (
            <DeckListItem key={deck.id} deck={deck} />
          ))}
        </div>
      )}

      {retiredCount > 0 && (
        <button
          className="btn ghost block"
          style={{ marginTop: 12 }}
          onClick={() => setShowRetired((v) => !v)}
        >
          {showRetired ? 'Hide' : 'Show'} {retiredCount} retired
        </button>
      )}

      {/*
        Registering sits in the corner rather than across the top.

        It was a full-width primary button above the list — the loudest thing
        on a screen whose job is showing decks, and pressed once every few
        weeks. In the corner it is always reachable however far the list has
        been scrolled, which the old one was not.
      */}
      <button className="fab" onClick={() => setAdding(true)} aria-label="Register a deck">
        <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
          <path
            d="M12 5v14M5 12h14"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {adding && <AddDeckSheet onClose={() => setAdding(false)} />}
    </>
  );
}

/** How many to name before the rest become a count. */
const NEEDS_SHOWN = 4;

/**
 * Decks that have not seen the table in a while.
 *
 * Spec 5, tier 3 — a nudge, not an obligation. There is no penalty for leaving
 * a deck in the box and nothing here asks anyone to play one; it exists because
 * "which of these have we not had out in ages" is a question the pool screen
 * can answer and a room full of people picking decks cannot.
 *
 * Measured in guild nights rather than games, and silent until the guild has
 * played enough of them for the number to mean anything. A league one evening
 * old reporting its whole pool as neglected would be both true and useless.
 */
function NeedsAGame() {
  const { state } = useStore();
  const neglected = useMemo(
    () => decksNeedingAGame(state.matches, state.decks),
    [state.matches, state.decks],
  );
  if (neglected.length === 0) return null;

  const shown = neglected.slice(0, NEEDS_SHOWN);
  const rest = neglected.length - shown.length;

  return (
    <div className="needs-game">
      <div className="needs-game-head">
        <h2>Needs a game</h2>
        <span className="hint num">{neglected.length}</span>
      </div>
      <div className="needs-game-rows">
        {shown.map(({ deck, nights, lastPlayed }) => (
          <button
            key={deck.id}
            className="needs-game-row"
            onClick={() => navigate({ name: 'deck', id: deck.id })}
          >
            <ColorPips colors={deck.colors} />
            <span className="needs-game-name">{deckLabel(deck)}</span>
            <span className="needs-game-when num">
              {/* A deck that has never been played says so. "5 nights" would
                  imply it once had a turn. */}
              {lastPlayed === null ? 'never' : `${nights} night${nights === 1 ? '' : 's'}`}
            </span>
          </button>
        ))}
      </div>
      {rest > 0 && (
        <p className="hint" style={{ margin: '8px 2px 0' }}>
          and {rest} more sitting out.
        </p>
      )}
    </div>
  );
}

function DeckListItem({ deck }: { deck: Deck }) {
  const { state } = useStore();
  const record = deckRecord(state.matches, deck.id);
  const ranked = isRanked(deck.placementGames);

  return (
    <button
      className="list-item"
      style={{ border: 0, width: '100%', textAlign: 'left', cursor: 'pointer' }}
      onClick={() => navigate({ name: 'deck', id: deck.id })}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0, flex: 1 }}>
        {/* The same face the seat cards use, so a pair reads as a pair
            wherever the deck appears. */}
        <DeckThumb deck={deck} size={42} />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 15,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              opacity: deck.retired ? 0.5 : 1,
            }}
          >
            {deckLabel(deck)}
          </div>
          {/*
            The commander, under the name the table calls it by.

            Only when there is a nickname to be under: without one the title is
            already the commander, and printing it twice is noise. Two people
            can bring Atraxa and mean entirely different decks, so the nickname
            is the deck's identity and this is the card it happens to be built
            around — which is worth being able to check without opening it.
          */}
          {deck.nickname?.trim() && (
            <div className="deck-commander">{commandersOf(deck).join(PAIR_JOIN)}</div>
          )}
          <div className="sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ColorPips colors={deck.colors} />
            <span className="num">{formatRecord(record)}</span>
            {deck.powerLevel && (
              <span className="pip num" title="power level">
                {roman(deck.powerLevel)}
              </span>
            )}
          </div>
        </div>
      </div>
      <span className="num" style={{ fontSize: 15, color: ranked ? 'var(--text)' : 'var(--muted)' }}>
        {/* Progress toward a rating, not a rating. */}
        {ranked ? display(deck.elo) : `${deck.placementGames}/${PLACEMENT_GAMES}`}
      </span>
    </button>
  );
}

export function ColorPips({ colors }: { colors: readonly ('W' | 'U' | 'B' | 'R' | 'G')[] }) {
  const sorted = sortColors(colors);
  if (sorted.length === 0) {
    return <span style={{ color: 'var(--muted)', fontSize: 11 }}>colourless</span>;
  }
  return (
    <span style={{ display: 'inline-flex', gap: 3 }}>
      {sorted.map((c) => (
        <span
          key={c}
          title={c}
          style={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: COLOR_HEX[c],
            border: '1px solid rgba(0,0,0,0.35)',
          }}
        />
      ))}
    </span>
  );
}

function AddDeckSheet({ onClose }: { onClose: () => void }) {
  const { state, createDeck, updateDeck } = useStore();
  const [pick, setPick] = useState<CommanderPick | null>(null);
  const [manualName, setManualName] = useState('');
  const [nickname, setNickname] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [powerLevel, setPowerLevel] = useState<PowerLevel | null>(null);
  const [busy, setBusy] = useState(false);
  /** Partner, Background, Friends forever — all the same shape: two commanders. */
  const [hasPartner, setHasPartner] = useState(false);
  const [partnerPick, setPartnerPick] = useState<CommanderPick | null>(null);
  const [partnerManual, setPartnerManual] = useState('');

  /**
   * A deck with no card behind it.
   *
   * A rebadged precon, a cube deck, a commander from a set Scryfall has not
   * caught up with. Everything the search normally supplies — the name, the
   * colour identity, the art, whether it may take a partner — has to be said
   * by hand, because there is nothing to ask.
   */
  const [custom, setCustom] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customColors, setCustomColors] = useState<Color[]>([]);
  const [customArt, setCustomArt] = useState<string | null>(null);
  const [artError, setArtError] = useState<string | null>(null);

  const name = custom ? customName.trim() : (pick?.name ?? manualName.trim());
  const partnerName = custom ? partnerManual.trim() : (partnerPick?.name ?? partnerManual.trim());

  async function chooseArt(file: File | undefined) {
    if (!file) return;
    setArtError(null);
    try {
      setCustomArt(await fileToDeckArt(file));
    } catch (err) {
      setArtError(err instanceof Error ? err.message : 'that image did not work');
    }
  }

  /**
   * Both identities together, which is what the deck may actually play.
   *
   * A pair is not two decks side by side — the commanders share a card pool,
   * and the colours it can hold are the union of theirs. Getting this wrong
   * would mean a Thrasios/Tymna deck showing as green when it is four.
   */
  const colors: Color[] = custom
    ? sortColors(customColors)
    : sortColors([
        ...new Set([...(pick?.colors ?? []), ...(hasPartner ? (partnerPick?.colors ?? []) : [])]),
      ]);

  async function save() {
    if (!name || busy) return;
    setBusy(true);
    try {
      const deck = await createDeck({
        name,
        nickname,
        colors,
        // A custom deck has no card, so none of the Scryfall fields apply. The
        // picture it does have travels with the record instead.
        scryfallId: custom ? null : (pick?.scryfallId ?? null),
        artCropUrl: custom ? null : (pick?.artCropUrl ?? null),
        customArt: custom ? customArt : null,
        partner:
          hasPartner && partnerName
            ? {
                name: partnerName,
                scryfallId: custom ? null : (partnerPick?.scryfallId ?? null),
                artCropUrl: custom ? null : (partnerPick?.artCropUrl ?? null),
              }
            : null,
        ownerId: ownerId || null,
        powerLevel,
      });

      // Spec 3b — download the bytes now, while there is still signal. A deck
      // registered at the kitchen table has to render in the shop basement.
      // Both commanders, because the banner draws both.
      try {
        if (pick?.artCropUrl) await writeArt(deck.id, await fetchArt(pick.artCropUrl));
        if (hasPartner && partnerPick?.artCropUrl) {
          await writeArt(`${deck.id}:partner`, await fetchArt(partnerPick.artCropUrl));
        }
        if (pick?.artCropUrl) await updateDeck(deck.id, { artCached: true });
      } catch {
        // No signal; warmArtCache picks it up on the next sync.
      }
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="Register a deck" onClose={onClose}>
      {/*
        Short on purpose.
        
        This drawer had grown a paragraph under every control explaining what
        the control was for, which is what you write while a thing is new and
        what nobody reads twice. The placeholders and the labels say it, the
        colours appearing say it, and the one line that survives says a thing
        the screen cannot show: where the rating starts.
      */}
      {/*
        Whether there is a card behind this at all.

        First, because it changes what every control below it is. A rebadged
        precon or a cube deck has no Scryfall entry, and the search returning
        nothing is not an error — it is the answer, and the app should have
        somewhere to put it.
      */}
      {/* A switch rather than a checkbox: these are two ways of registering a
          deck, not a property of one, and a tick box makes the second look
          like an afterthought bolted to the first. */}
      <div className="segmented" role="tablist" aria-label="How to register">
        <span
          className="segmented-thumb"
          style={{ transform: `translateX(${custom ? '100%' : '0%'})` }}
        />
        {(
          [
            [false, 'Sanctioned commander'],
            [true, 'Custom commander'],
          ] as const
        ).map(([mode, label]) => (
          <button
            key={label}
            className="segmented-option"
            role="tab"
            aria-selected={custom === mode}
            onClick={() => {
              if (custom === mode) return;
              setCustom(mode);
              // Whichever half is being left behind is cleared, so nothing from
              // the other mode rides along into the saved deck.
              setPick(null);
              setManualName('');
              setPartnerPick(null);
              setPartnerManual('');
              setHasPartner(false);
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {custom ? (
        <>
          <label className="field">
            <span>Commander</span>
            <input
              className="input"
              placeholder="The name on the card"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
            />
          </label>

          {/* Normally this comes free from the card. Here it has to be said,
              and it matters: identity is what the ladder filters and sorts by. */}
          <div className="field">
            <span>Colour identity</span>
            <div className="color-filter" style={{ marginTop: 2 }}>
              {COLORS.map((c) => (
                <button
                  key={c}
                  className="color-dot"
                  aria-pressed={customColors.includes(c)}
                  aria-label={c}
                  style={{ background: COLOR_HEX[c] }}
                  onClick={() =>
                    setCustomColors((cur) =>
                      cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c],
                    )
                  }
                />
              ))}
            </div>
          </div>

          <div className="field">
            <span>Art (optional)</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {customArt ? (
                <img
                  src={customArt}
                  alt=""
                  style={{ width: 84, height: 60, objectFit: 'cover', borderRadius: 8 }}
                />
              ) : (
                <span
                  aria-hidden="true"
                  style={{
                    width: 84,
                    height: 60,
                    borderRadius: 8,
                    border: '1px dashed var(--hairline)',
                    background: identityGradient(colors, '145deg'),
                    opacity: 0.7,
                  }}
                />
              )}
              <label className="btn ghost" style={{ cursor: 'pointer' }}>
                {customArt ? 'Change' : 'Choose'}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => void chooseArt(e.target.files?.[0])}
                />
              </label>
              {customArt && (
                <button className="invite-action" onClick={() => setCustomArt(null)}>
                  Remove
                </button>
              )}
            </div>
            {artError && <p className="error">{artError}</p>}
          </div>
        </>
      ) : (
        /* The search owns its own label — it has to, because the spinner sits in
           it while a card is being fetched. Wrapping it in a second one is what
           printed "Commander" twice. */
        <CommanderSearch onPick={setPick} onManual={(n) => { setManualName(n); setPick(null); }} />
      )}

      {/*
        Partners, backgrounds, friends forever — all one deck with two
        commanders, and all of them printed on the card.

        Only offered once a commander that can actually take one has been
        picked. Atraxa cannot partner with anything, so the checkbox sitting
        under her was an invitation to record something illegal — and it was
        there before any card had been chosen at all, which is when the app
        knows least.
      */}
      {/* Custom decks are taken at their word: there is no card to check, so
          the question is asked rather than answered. */}
      {(custom ? Boolean(name) : Boolean(pick?.pairs)) && (
        <label className="check-row">
          <input
            type="checkbox"
            checked={hasPartner}
            onChange={(e) => {
              setHasPartner(e.target.checked);
              if (!e.target.checked) {
                setPartnerPick(null);
                setPartnerManual('');
              }
            }}
          />
          <span className="check-label">Partner commander</span>
        </label>
      )}

      {hasPartner &&
        (custom ? (
          <label className="field">
            <span>Partner</span>
            <input
              className="input"
              placeholder="The second commander"
              value={partnerManual}
              onChange={(e) => setPartnerManual(e.target.value)}
            />
          </label>
        ) : (
          <CommanderSearch
            key="partner"
            label="Partner"
            placeholder="Thrasios, Triton Hero"
            onPick={setPartnerPick}
            onManual={(n) => {
              setPartnerManual(n);
              setPartnerPick(null);
            }}
          />
        ))}

      {/* The identity, shown rather than described — pips appearing when a card
          is picked is the whole explanation. */}
      {colors.length > 0 && (
        <div className="field-inline">
          <span>Identity</span>
          <ColorPips colors={colors} />
        </div>
      )}

      <label className="field">
        {/* What the group calls it out loud, which is not quite a nickname —
            and is the name the deck goes by everywhere in the app once set. */}
        <span>Table name</span>
        <input
          className="input"
          placeholder={name || 'Atraxa'}
          maxLength={MAX_DECK_NICKNAME}
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
        />
      </label>

      {/* Descriptive only — it never touches the rating. The rating is the
          measurement; letting a self-declared number feed it would measure the
          claim instead. */}
      <div className="field">
        <span>Power level</span>
        <div className="chips">
          {POWER_LEVELS.map((level) => (
            <button
              key={level}
              className="chip num"
              aria-pressed={powerLevel === level}
              onClick={() => setPowerLevel(powerLevel === level ? null : level)}
            >
              {roman(level)}
            </button>
          ))}
        </div>
        {/* The starting rating used to be printed here. It is a number nobody
            can act on — the bracket is the only input, and a deck's rating is
            measured from its games within five of them anyway. */}
      </div>

      {/*
        Faces, not a dropdown.

        The same picker the seats use, for the same reason: a guild is four
        people you know by sight, and a native select made you open a system
        menu and read a list of names to say something you could have pointed
        at. It also cannot show a photograph, which is most of what identifies
        somebody here.
      */}
      <div className="field">
        {/* The word the deck page uses for the same person, so the label you
            fill in and the label you read back are the same one. */}
        <span>Architect</span>
        <div className="pick-grid" style={{ marginTop: 2 }}>
          {state.pilots
            .filter((p) => !p.retired)
            .map((p) => (
              <button
                key={p.id}
                className={`pick${ownerId === p.id ? ' picked' : ''}`}
                // Tapping the chosen one again clears it, which is how every
                // other picker in the app behaves — and is how a deck nobody
                // built ends up with nobody against it.
                onClick={() => setOwnerId(ownerId === p.id ? '' : p.id)}
              >
                <RankedAvatar pilot={p} size={44} />
                <span className="pick-name">{p.name}</span>
              </button>
            ))}
        </div>
      </div>

      <div className="btn-row" style={{ marginTop: 6 }}>
        <button className="btn ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn primary"
          disabled={!name || (hasPartner && !partnerName) || busy}
          onClick={() => void save()}
        >
          {busy ? <span className="spinner" /> : 'Register'}
        </button>
      </div>
    </Sheet>
  );
}
